import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Star } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n/LanguageContext";
import { track } from "../lib/track";

const TrustStars = () => (
  <div className="mt-3 flex gap-1" data-testid="trustpilot-stars">
    {[0, 1, 2, 3, 4].map((i) => (
      <span key={i} className="flex h-5 w-5 items-center justify-center bg-[#00B67A]">
        <Star className="h-3.5 w-3.5 fill-white text-white" strokeWidth={0} />
      </span>
    ))}
  </div>
);

export const ActionCard = ({ id, index, tile: Tile, accent, url, internal }) => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const copy = t.actions[id];

  const onClick = (e) => {
    track(id, lang);
    if (internal) {
      e.preventDefault();
      navigate(url);
    } else if (!url) {
      e.preventDefault();
      toast(copy.title, { description: t.actions.soon });
    }
  };

  return (
    <motion.a
      href={url || "#"}
      target={url && !internal ? "_blank" : undefined}
      rel="noopener noreferrer"
      onClick={onClick}
      data-testid={`action-card-${id}`}
      whileTap={{ scale: 0.975 }}
      className="action-card group relative flex items-center gap-4 overflow-hidden rounded-[28px] border border-white/[0.07] bg-surface/70 p-4 pr-5 backdrop-blur-xl transition-colors duration-500 hover:border-white/15 sm:gap-5 sm:p-5"
      style={{ "--accent": accent }}
    >
      <span className="pointer-events-none absolute -left-10 top-1/2 h-32 w-32 -translate-y-1/2 rounded-full opacity-0 blur-3xl transition-opacity duration-700 group-hover:opacity-40" style={{ background: accent }} />
      <span className="relative transition-transform duration-500 group-hover:-rotate-6 group-hover:scale-105" data-testid={`action-logo-${id}`}>
        <Tile />
      </span>
      <span className="relative min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="font-mono text-[11px] text-zinc-400">{index}</span>
          {copy.badge && (
            <span data-testid={`badge-${id}`} className="rounded-full border border-emerald-400/40 bg-emerald-400/15 px-2 py-0.5 font-mono text-[10px] font-semibold tracking-[0.15em] text-emerald-300">
              {copy.badge}
            </span>
          )}
        </span>
        <span className="mt-1 block font-display text-lg font-semibold leading-tight text-zinc-50 sm:text-xl">{copy.title}</span>
        <span className="mt-1 block text-sm leading-snug text-zinc-400">{copy.sub}</span>
        {copy.risk && (
          <span data-testid={`risk-warning-${id}`} className="mt-1.5 block text-[12px] leading-snug text-zinc-400">
            {copy.risk}
          </span>
        )}
        {id === "trustpilot" && <TrustStars />}
      </span>
      <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/10 text-zinc-300 transition-[background-color,color,transform] duration-500 group-hover:rotate-45 group-hover:bg-gold group-hover:text-ink">
        <ArrowUpRight className="h-5 w-5" />
      </span>
    </motion.a>
  );
};
